/**
 * CompanyLoader - Loads companies using React Query
 * 
 * Constitution Section 19.1: State Management Separation
 * Constitution Section 19.2: Data Fetching Patterns
 * 
 * This component uses React Query to fetch companies and manages
 * UI state (activeCompany) in Zustand store.
 */

import React, { useEffect } from 'react';
import { useCompanyStore } from '../stores/useCompanyStore';
import { useAuth0 } from '@auth0/auth0-react';
import { useCompanies } from '../hooks/queries/useCompanies';

export default function CompanyLoader() {
  const { isAuthenticated } = useAuth0();
  const { companies, isLoading, error } = useCompanies();
  const { 
    activeCompany, 
    hydrateActiveCompany, 
    setDefaultActiveCompany,
    setActiveCompany 
  } = useCompanyStore();

  // Hydrate active company from localStorage when companies are loaded
  useEffect(() => {
    if (!isAuthenticated || isLoading || companies.length === 0) {
      return;
    }

    // CRITICAL: Guard against redundant updates - check if active company is already set
    // SSR-safe: localStorage only available in browser
    if (typeof window === 'undefined') {
      return;
    }

    const currentState = useCompanyStore.getState();
    const savedId = localStorage.getItem('activeCompanyId');
    
    // If activeCompany is already set and matches saved ID, skip hydration
    if (currentState.activeCompany && savedId) {
      if (currentState.activeCompany.id === Number(savedId)) {
        return; // Already hydrated, no need to update
      }
    }

    // Hydrate active company from localStorage
    hydrateActiveCompany(companies);

    // If no active company after hydration, set the first one as default
    const stateAfterHydration = useCompanyStore.getState();
    if (!stateAfterHydration.activeCompany && companies.length > 0) {
      setDefaultActiveCompany(companies);
    }
  }, [isAuthenticated, isLoading, companies, hydrateActiveCompany, setDefaultActiveCompany]);

  return null; // This component doesn't render anything
}
